import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import DialogContent from "@mui/material/DialogContent";
import Selector from "./reactSelector";
import Grid from "@mui/material/Grid";
import "../../index.css";
import Divider from "@mui/material/Divider";
import spinner from "../assign-prescriptions/images/spinner.gif";
import { withTranslation } from "react-i18next";
import { Button, Paper } from "@mui/material";
import { toast } from "react-toastify";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { writeAuditLog } from '../common/apis';

const styles = () => ({
    root: {
        width: "100%",
        height: "80vh",
    },
    twoChildren: {
        display: "flex",
        justifyContent: "space-between",
    },
    leftChild: {
        display: "flex",
        flexDirection: "column",
        width: "25%",
        margin: "1%",
        gap: "12px",
    },
    rightChild: {
        display: "flex",
        flexDirection: "column",
        width: "25%",
        margin: "1%",
        gap: "12px",
    },
    spinner: {
        position: "absolute",
        background: "rgba(255,255,255,.4)",
        width: "100%",
        height: "100%",
        zIndex: "9999",
        top: 0,
        left: 0,
    },
    spinnerImage: {
        top: "calc(50% - 32.5px)",
        left: "calc(50% - 65px)",
        position: "absolute",
        width: "130px",
        userSelect: "none",
    },
    primaryButton: {
        background: "#6cca98",
        color: "#fff",
        "&:hover": {
            background: "#6cca98",
            color: "#fff",
        },
        "&:disabled": {
            color: "rgba(0, 0, 0, 0.38)",
            cursor: "not-allowed",
            background: "#E4E5E8",
        },
    },
});

class Rolepermissions extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            parent_account_id: null,
            parent_role_id: null,
            parent_permissionData: [],
            parent_CheckedPermissionParentCategory: [],
            parent_CheckedPermissionSubCategory: [],

            child_account_id: null,
            child_role_id: null,
            child_permissionData: [],
            child_CheckedPermissionParentCategory: [],
            child_CheckedPermissionSubCategory: [],

            accountValues: [],
            roleValues: [],
            loader: false,
        };
    }

    componentDidMount() {
        this.fetchInitialData();
    }

    getAccountLabel = (accountId) => {
        const account = (this.state.accountValues || []).find(
            (item) => String(item.value) === String(accountId)
        );
        return account ? `${account.label} (${account.value})` : accountId;
    };

    getRoleLabel = (roleId) => {
        const role = (this.state.roleValues || []).find(
            (item) => String(item.value) === String(roleId)
        );
        return role ? this.props.t(role.label) : roleId;
    };

    fetchInitialData = async () => {
        this.setState({ loader: true });

        const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession
            .idToken.jwtToken;
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };

        const [accountsRes, rolesRes] = await Promise.all([
            fetch(process.env.REACT_APP_BASEURL + "accounts", {
                method: "GET",
                headers,
            }),
            fetch(process.env.REACT_APP_BASEURL + "roles", {
                method: "GET",
                headers,
            }),
        ]);

        const accounts = await accountsRes.json();
        const roles = await rolesRes.json();

        this.setState({
            accountValues: accounts.map((a) => ({ value: a.id, label: a.name })),
            roleValues: roles.map((r) => ({
                value: r.id,
                label: this.props.t(r.name),
            })),
            loader: false,
        });
    };

    fetchPermissions = (type) => {
        const account_id = this.state[`${type}_account_id`];
        const role_id = this.state[`${type}_role_id`];

        if (!account_id || !role_id) return;

        this.setState({ loader: true });

        fetch(process.env.REACT_APP_BASEURL + "permissions", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken
                    .jwtToken
                    }`,
            },
            body: JSON.stringify({ accountid: account_id, roleid: role_id }),
        })
            .then((res) => res.json())
            .then((data) => {
                const uniqueCategories = this.getUnique(data, "entity_type_id");
                const parentCategories = uniqueCategories.map((item) => ({
                    value: item.entity_type_id,
                    label: item.entity_name,
                    status: item.status,
                }));
                const subCategories = data.map((item) => ({
                    parentvalue: item.entity_type_id,
                    parenetlabel: item.entity_name,
                    value: item.id,
                    label: item.name,
                    status: item.status,
                }));

                subCategories.sort((a, b) => a.label.localeCompare(b.label));
                parentCategories.sort((a, b) => a.label.localeCompare(b.label));

                this.setState({
                    [`${type}_permissionData`]: data,
                    [`${type}_CheckedPermissionParentCategory`]: parentCategories,
                    [`${type}_CheckedPermissionSubCategory`]: subCategories,
                    loader: false,
                });
            });
    };

    getUnique = (arr, comp) => {
        const unique = arr
            .map((e) => e[comp])
            .map((e, i, final) => final.indexOf(e) === i && i)
            .filter((e) => arr[e])
            .map((e) => arr[e]);
        return unique;
    };

    handleParentChange = (event) => {
        const { id, value } = event.target;
        this.setState({ [`parent_${id}`]: value }, () => {
            if (id === "role_id") this.fetchPermissions("parent");
        });
    };

    handleChildChange = (event) => {
        const { id, value } = event.target;
        this.setState({ [`child_${id}`]: value }, () => {
            if (id === "role_id") this.fetchPermissions("child");
        });
    };

    isDifferentPermission = (sub, type) => {
        const compareType = type === "parent" ? "child" : "parent";
        const compareSubCategories =
            this.state[`${compareType}_CheckedPermissionSubCategory`] || [];

        const match = compareSubCategories.find(
            (c) => c.value === sub.value && c.status === sub.status
        );

        return !match;
    };

    renderPermissions = (type) => {
        const { t } = this.props;
        const parentCategories =
            this.state[`${type}_CheckedPermissionParentCategory`] || [];
        const subCategories =
            this.state[`${type}_CheckedPermissionSubCategory`] || [];

        return parentCategories.map((category) => (
            <React.Fragment key={category.value}>
                <Grid
                    container
                    item
                    xs={12}
                    direction="row"
                    style={{ marginTop: "10px" }}
                >
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                        {t(category.label)}
                    </Typography>
                </Grid>

                <Grid container spacing={1} sx={{ pl: 2 }}>
                    {subCategories
                        .filter((sub) => sub.parentvalue === category.value)
                        .map((sub, index) => (
                            <Grid item xs={6} md={4} key={index}>
                                <Typography variant="body2">
                                    <Checkbox
                                        checked={sub.status}
                                        disabled
                                        size="small"
                                        style={{
                                            color: this.isDifferentPermission(sub, type)
                                                ? "#0ae"
                                                : undefined,
                                        }}
                                    />
                                    {t(sub.label)}
                                </Typography>
                            </Grid>
                        ))}
                </Grid>
            </React.Fragment>
        ));
    };

    handlePushPermissions = async () => {
        const {
            parent_account_id,
            child_account_id,
            parent_role_id,
            child_role_id,
        } = this.state;

        if (!parent_account_id || !child_account_id) return;

        if (parent_account_id === child_account_id) {
            toast.error("From and To accounts must be different.");
            return;
        }

        this.setState({ loader: true });

        try {
            const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession
                .idToken.jwtToken;

            const res = await fetch(
                process.env.REACT_APP_BASEURL + "inheritPermissions",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        parentAccountId: parent_account_id,
                        accountId: child_account_id,
                    }),
                }
            );

            const result = await res.json();


            if (result.success) {
                toast.success("Permissions pushed successfully.");

                const parentLabel = this.getAccountLabel(parent_account_id);
                const childLabel = this.getAccountLabel(child_account_id);
                const parentRoleLabel = parent_role_id
                    ? this.getRoleLabel(parent_role_id)
                    : 'all roles';
                const childRoleLabel = child_role_id
                    ? this.getRoleLabel(child_role_id)
                    : 'all roles';

                writeAuditLog(
                    `Permissions pushed from '${parentLabel}' to '${childLabel}'. [PERMISSION_CHANGES:${JSON.stringify({
                        type: 'push',
                        fromAccount: parentLabel,
                        toAccount: childLabel,
                        fromRole: parentRoleLabel,
                        toRole: childRoleLabel,
                    })}]`,
                    'Push Permissions'
                );
            } else {
                toast.error("Failed to push permissions.");
            }
        } catch (err) {
            toast.error("Failed to push permissions.");
            console.error(err);
        } finally {
            this.setState({
                loader: false,
                parent_account_id: null,
                parent_role_id: null,
                parent_permissionData: [],
                parent_CheckedPermissionParentCategory: [],
                parent_CheckedPermissionSubCategory: [],

                child_account_id: null,
                child_role_id: null,
                child_permissionData: [],
                child_CheckedPermissionParentCategory: [],
                child_CheckedPermissionSubCategory: [],
            });
        }
    };

    render() {
        const { classes, t } = this.props;
        const { parent_permissionData, child_permissionData } = this.state;

        return (
            <div className={classes.root}>
                {this.state.loader && (
                    <div className={classes.spinner}>
                        <img className={classes.spinnerImage} src={spinner} alt="loading" />
                    </div>
                )}

                <div className={classes.twoChildren}>
                    <div className={classes.leftChild}>
                        <Selector
                            name="accounts"
                            id="account_id"
                            label={t("Role.SelectParentAccount")}
                            values={this.state.accountValues}
                            selected={this.state.parent_account_id}
                            onBlur={this.handleParentChange}
                        />
                        <Selector
                            name="roles"
                            id="role_id"
                            label={t("Role.SelectRole")}
                            values={this.state.roleValues}
                            selected={this.state.parent_role_id}
                            onBlur={this.handleParentChange}
                        />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "20%" }}>
                        <ArrowForwardIcon style={{ fontSize: 40, color: "#0ae" }} />
                    </div>
                    <div className={classes.rightChild}>
                        <Selector
                            name="accounts"
                            id="account_id"
                            label={t("Role.SelectChildAccount")}
                            values={this.state.accountValues}
                            selected={this.state.child_account_id}
                            onBlur={this.handleChildChange}
                        />
                        <Selector
                            name="roles"
                            id="role_id"
                            label={t("Role.SelectRole")}
                            values={this.state.roleValues}
                            selected={this.state.child_role_id}
                            onBlur={this.handleChildChange}
                        />
                    </div>
                </div>

                <Divider sx={{ height: "10px" }} />

                {parent_permissionData.length > 0 || child_permissionData.length > 0 ? (
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                {parent_permissionData.length > 0 ? (
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                        {this.renderPermissions("parent")}
                                    </Paper>
                                ) : (
                                    ""
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                {child_permissionData.length > 0 ? (
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                        {this.renderPermissions("child")}
                                    </Paper>
                                ) : (
                                    ""
                                )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                ) : (
                    ""
                )}
                {parent_permissionData.length > 0 && child_permissionData.length > 0 ? (
                    <div
                        style={{ display: "flex", justifyContent: "end", padding: "10px" }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            onClick={this.handlePushPermissions}
                            disabled={
                                !this.state.parent_account_id ||
                                !this.state.parent_role_id ||
                                !this.state.child_account_id ||
                                !this.state.child_role_id
                            }
                        >
                            {t("Role.Push")}
                        </Button>
                    </div>
                ) : (
                    ""
                )}
            </div>
        );
    }
}

Rolepermissions.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(
    withTranslation()(Rolepermissions)
);



// WEBPACK FOOTER //
// ./src/components/InheritPermission/index.js